import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';
import Notifications from '../../../components/notifications';

import { makeStyles, ThemeProvider as MUIThemeProvider } from '@mui/styles';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine';
import CSSBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Themes from '../../../data/themes';
import { User } from '../../../data/auth-service';
import UsersService from '../../../data/users-service';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: 25,
    },
    fullWidth: {
        width: '100%',
    },
    passBold: {
        color: theme.palette.secondary.main,
        marginLeft: '5px',
        marginRight: '5px',
    },
    listItem: {
        display: 'block',
    },
    passContainer: {
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingTop: '20px',
    }
}));

const PassReset = () => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Themes.DarkTheme}>
                <MUIThemeProvider theme={Themes.DarkTheme}>
                    <Reset />
                </MUIThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const Reset = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [, setExpired] = useState(false);
    const [password, setPassword] = useState({
        password: '',
        confirm: '',
        token: '',
        current_password: '',
        username: '',
    });
    const user = User();
    const passwordForm = useValidate({});
    const [original, setOriginal] = useState(_cloneDeep(password));
    const [changes, setChanges] = useState(false);

    useEffect(() => {
        setChanges(!_isEqual(password, original));
    }, [password]);

    useEffect(() => {
        setExpired(user.password_expired);
    }, [user]);

    useEffect(() => {
        setPassword({
            ...password,
            username: routeParams.username,
            token: routeParams.token
        });
    }, []);

    const handleUpdatePasswordData = (e, validID, valid) => {
        passwordForm.set({ ...passwordForm.get(), [validID]: valid });

        setPassword({
            ...password,
            [e.target.name]: e.target.value,
        });
    };

    const handleSetPassword = () => {
        UsersService.setPassword(routeParams.username, password).then(() => {
            toast.success('Password reset successfully');
            navigate('/signin');
        }, err => {
            console.error(err);
            toast.error('Password reset failed. Please try again.');
        });
    };

    const handleCancel = () => {
        navigate('/signin');
    };

    return (
        <ThemeProvider theme={Themes.DarkTheme}>
            <Notifications />
            <Box maxWidth="lg" className={classes.passContainer} color="text.primary">
                <CSSBaseline />
                <Grid container>
                    <Grid item xs={12}>
                        <Box textAlign="center" mt={30} mb={5}>
                            <img aria-label="logo" src="/images/logo-new.png" />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box mt={2} p={3}>
                                <Grid container spacing={2}>
                                    <Grid container item xs={8} spacing={2} alignContent="flex-start">
                                        <Grid item xs={12}>
                                            <Typography variant="h5">Set Password - {routeParams.username}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Validator
                                                valueType="password"
                                                errorText="Password is invalid"
                                                label="New Password"
                                                type="password"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                value={password.password}
                                                name="password"
                                                onChange={handleUpdatePasswordData}
                                            />
                                            <Validator
                                                valueType="password"
                                                equal={password.password}
                                                errorText="Password does not match"
                                                label="Confirm Password"
                                                type="password"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                value={password.confirm}
                                                name="confirm"
                                                onChange={handleUpdatePasswordData}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                variant="contained"
                                                className={classes.buttonRight}
                                                aria-label="set password"
                                                onClick={handleSetPassword}
                                                disabled={!(checkValidity(passwordForm.get()) && changes)}
                                            >Set Password</Button>
                                            <Button variant="contained" className={classes.buttonRight} aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography>Passwords must:</Typography>
                                        <List>
                                            <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more uppercase </b> characters (A-Z)</ListItem>
                                            <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more lowercase </b> characters (a-z)</ListItem>
                                            <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more digits </b> (0-9)</ListItem>
                                            <ListItem className={classes.listItem}>contain <b className={classes.passBold}> one or more non-alphanumeric </b> characters: - + _ ! @ # $ % ^ &amp; * . , ?</ListItem>
                                            <ListItem className={classes.listItem}>have <b className={classes.passBold}>8 characters</b> or more</ListItem>
                                            <ListItem className={classes.listItem}>have <b className={classes.passBold}>not been used</b> as one of previous passwords</ListItem>
                                        </List>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
};

export default PassReset;
